import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  MenuItem,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getEmissionModel } from '../../emission-coefficients/emission-coefficients'
import {
  BuildingFragment,
  ProjectFragment,
  EmissionType,
} from '../../generated/graphql'
import { theme } from '../../theme'
import { sortByProperty } from '../../util/array-utility'
import { Dialog } from '../dialog/Dialog'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { validateEmission } from './emission-util'
import ControlledEmissionValue from './ControlledEmissionValue'

export type FormValues = {
  buildingId: string
  emissionType: EmissionType
  projectVersionId: string
  value: string
  inherentValue: string
}

type Props = {
  project: ProjectFragment
  isOpen: boolean
  isCreate: boolean
  onClose: () => void
  onSubmit: (values: FormValues) => void
  onDelete: () => void
  defaultValues: Partial<FormValues>
}

export default function EmissionDialog({
  project,
  isOpen,
  isCreate,
  onClose,
  onSubmit,
  onDelete,
  defaultValues,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const [buildings, setBuildings] = useState<BuildingFragment[]>([])
  const [isEmissionValid, setIsEmissionValid] = useState(true)
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingFragment>()
  const emissionModel = getEmissionModel(project)

  const {
    control,
    errors,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
  } = useForm<FormValues>({
    defaultValues,
  })

  useEffect(() => {
    reset(defaultValues)
    const initialBuildings = project.versions.find(
      (version) => version.id === defaultValues.projectVersionId,
    )?.buildings
    if (initialBuildings) {
      setBuildings(initialBuildings)
      const initiallySelectedBuilding = defaultValues.buildingId
        ? initialBuildings?.find((b) => b.id === defaultValues.buildingId)
        : initialBuildings?.find(Boolean)
      if (initiallySelectedBuilding) {
        setSelectedBuilding(initiallySelectedBuilding)
        setValue('buildingId', initiallySelectedBuilding.id)
      }
    }
    // This useEffect should only be triggered by change in defaultValues, which is already dependent on project
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues])

  const formValues = useWatch<FormValues>({ control })

  useEffect(() => {
    const { projectVersionId, buildingId, emissionType } = formValues
    if (!isCreate) {
      setIsEmissionValid(true)
      return
    }
    if (projectVersionId && buildingId && emissionType && isOpen) {
      const isEmissionNew = validateEmission(
        project.versions,
        projectVersionId,
        buildingId,
        emissionType,
      )
      setIsEmissionValid(isEmissionNew)
    }
    // No need to trigger validation unless form values are updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues])

  const emissionType = useWatch({ name: 'emissionType', control })

  const dialogEmissionTypes = Object.keys(EmissionType).filter(
    (type) => type !== EmissionType.Energy,
  )

  const title = isCreate
    ? t('carbonFootprint.dialog.title')
    : t('carbonFootprint.dialog.edit')

  const onProjectVersionIdChange = (versionId: string) => {
    const versionBuildings = project.versions.find(
      (version) => version.id === versionId,
    )?.buildings
    if (!versionBuildings || versionBuildings.length === 0) {
      setError('projectVersionId', {
        message: t('carbonFootprint.dialog.validationNoBuildings'),
      })
    } else {
      clearErrors('projectVersionId')
    }
    setBuildings(versionBuildings ?? [])
    setValue('buildingId', versionBuildings?.find(Boolean)?.id ?? '')
  }

  const isProjectVersionValid = buildings.length !== 0

  const onBuildingChange = (buildingId: string) => {
    const building = buildings.find((b) => b.id === buildingId)
    if (building) {
      setSelectedBuilding(building)
    }
  }

  return (
    <>
      {project && (
        <Dialog title={title} open={isOpen} onClose={onClose}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent dividers>
              <FormGroup>
                <ControlledTextField
                  control={control}
                  name="emissionType"
                  label={t('carbonFootprint.dialog.emissionType')}
                  required
                  select
                >
                  {dialogEmissionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`emissionType.${type}`)}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </FormGroup>

              <FormGroup>
                <ControlledTextField
                  control={control}
                  name="projectVersionId"
                  required
                  error={errors.projectVersionId}
                  label={t('carbonFootprint.dialog.projectVersion')}
                  onChange={(event) =>
                    onProjectVersionIdChange(event.target.value)
                  }
                  select
                >
                  {sortByProperty(project.versions, 'name').map((version) => (
                    <MenuItem key={version.id} value={version.id}>
                      {version.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </FormGroup>

              <FormGroup>
                <ControlledTextField
                  control={control}
                  name="buildingId"
                  label={t('carbonFootprint.dialog.building')}
                  required
                  select
                  disabled={!isProjectVersionValid}
                  onChange={(event) => onBuildingChange(event.target.value)}
                >
                  {sortByProperty(buildings, 'name').map((building) => (
                    <MenuItem key={building.id} value={building.id}>
                      {building.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </FormGroup>
              {selectedBuilding && (
                <ControlledEmissionValue
                  control={control}
                  emissionType={emissionType as EmissionType}
                  errors={errors}
                  disabled={!isProjectVersionValid}
                  area={selectedBuilding.area}
                  inspectionLengthInYears={
                    emissionModel.inspectionLengthInYears
                  }
                  setValue={setValue}
                  prefillValue={defaultValues.value}
                />
              )}
              {!isEmissionValid && (
                <FormHelperText error>
                  {t('validation.emissionDuplicate')}
                </FormHelperText>
              )}
            </DialogContent>
            <DialogActions>
              <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
                <Box>
                  {!isCreate && (
                    <Button
                      data-cy="delete-emission"
                      variant="contained"
                      color="secondary"
                      onClick={onDelete}
                    >
                      {t('common.remove')}
                    </Button>
                  )}
                </Box>

                <Box flexGrow={1} />
                <Button variant="contained" onClick={onClose}>
                  {t('common.cancel')}
                </Button>

                <Button
                  disabled={!isProjectVersionValid || !isEmissionValid}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  {isCreate ? t('common.create') : t('common.saveChanges')}
                </Button>
              </Box>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </>
  )
}
